import {
  CreateDeliveryResponse,
  CreateDeliveryResponseDto,
  DeliveriesWithPageCount,
  Delivery,
  DeliveryPriceEstimation,
  DeliveryRequest,
  DeliveryStatus,
  DriverDetails,
  Passenger,
  Vehicle
} from '../models/deliveries';
import {
  ActiveDeliveriesPageResponseDto,
  ActiveUserDeliveryV2Dto,
  CaptainDetailsDto,
  DeliveryDetailsDto,
  DeliveryDto,
  DeliveryOptionDto,
  DeliveryPriceEstimationRequestDto,
  DeliveryPriceEstimationResponseDto,
  DeliveryReport,
  DeliveryReportDto,
  DeliveryRequestDto,
  DriverDetailsDto,
  OrderType,
  PassengerDto,
  PastDeliveryDto,
  VehicleDto
} from '../dtos/deliveries';
import { fromDropOffDto, fromDropOffDtoV2, fromDto, fromDtoV2 } from './locations';
import { DropOffLocationDtoV2, LatLng, SearchLocation } from '../models/locations';
import { DeliveryOption } from '../../types';

export const getCODString = (amount: number) => {
  return `PAY THE MERCHANT ${amount} / COLLECT FROM CUSTOMER ${amount}`;
};

export const getCODFromDeliveryDetails = (deliveryDetails: string) => {
  const matches = deliveryDetails.match(/Pay the merchant (\d+[,.]?\d*)/i);
  return Number(matches?.[1]) || undefined;
};

export const getOrderIdString = (orderId: string) => {
  return `Order ID# ${orderId}`;
};

export const getOrderIdFromDeliveryDetails = (deliveryDetails: string) => {
  const matches = deliveryDetails.match(/Order ID# ([\S ]+)/i);
  return matches?.[1] || '';
};

/**
 * Existing booking statuses and how they are mapped to Express:
 * UPCOMING - Searching for Captain
 * DRIVER_ASSIGNED, DRIVER_COMING - Captain Coming
 * DRIVER_HERE - Captain Arrived at Outlet
 * TRIP_STARTED - Delivering order
 * TRIP_ENDED - Delivered
 * CANCELED - Cancelled
 *
 * I am not sure if these are actually used: I've never seen those, even in cases where booking was cancelled due to "capacity" or "no drivers".
 * Nevertheless, they are all going to be mapped to "Cancelled"
 * REJECTED_DUE_TO_CAPACITY
 * REJECTED_DUE_TO_BLACK_OUT
 * REJECTED_DUE_TO_NO_DRIVERS
 *
 * I am not these are used either. Both will be mapped to "Delivering order"
 * MULTI_STOP_WAY_POINT_DEPARTURE, MULTI_STOP_WAY_POINT_ARRIVAL
 */
function mapStatus(deliveryStatus: string): DeliveryStatus {
  switch (deliveryStatus) {
    case 'UPCOMING':
      return DeliveryStatus.Pending;
    case 'CAPTAIN_ASSIGNED':
    case 'DRIVER_ASSIGNED':
    case 'CAPTAIN_COMING':
    case 'DRIVER_COMING':
      return DeliveryStatus.Assigned;
    case 'CAPTAIN_HERE':
    case 'DRIVER_HERE':
      return DeliveryStatus.AtPickup;
    case 'DELIVERY_STARTED':
    case 'TRIP_STARTED':
    case 'MULTI_STOP_WAY_POINT_DEPARTURE':
    case 'MULTI_STOP_WAY_POINT_ARRIVAL':
      return DeliveryStatus.InProgress;
    case 'DELIVERY_ENDED':
    case 'TRIP_ENDED':
      return DeliveryStatus.Delivered;
    case 'REJECTED_DUE_TO_CAPACITY':
    case 'REJECTED_DUE_TO_BLACK_OUT':
    case 'REJECTED_DUE_TO_NO_DRIVERS':
    case 'REJECTED':
    case 'CANCELED':
      return DeliveryStatus.Cancelled;
    case 'CAPTAIN_NOT_FOUND':
      return DeliveryStatus.CNF;
    default:
      return deliveryStatus as DeliveryStatus;
  }
}

export function dtoToDriverDetails(driverDetails: DriverDetailsDto): DriverDetails {
  return {
    driverName: driverDetails.driver_name,
    driverNumber: driverDetails.driver_number,
    notesToDriver: driverDetails.notes_to_driver,
    driverPicture: driverDetails.driver_picture
  };
}

export function dtoToPassenger(passenger: PassengerDto): Passenger {
  return {
    firstName: passenger.firstName,
    lastName: passenger.lastName,
    phoneNumber: passenger.phoneNumber
  };
}

export function dtoToVehicle(vehicle: VehicleDto): Vehicle {
  return {
    vehicleModel: `${vehicle.color} ${vehicle.make} ${vehicle.model}`,
    plateNumber: vehicle.license_plate
  };
}

const singleDtoToDelivery = (dto: DeliveryDto): Delivery => {
  const delivery: Delivery = {
    id: dto.solutions_reference,
    bookingId: dto.booking_id,
    status: mapStatus(dto.status),
    pickUpTime: new Date(dto.booking_time)
  };

  if (dto.eta) {
    delivery.eta = dto.eta; // seconds
  }

  if (dto.pickup_details) {
    delivery.pickupLocation = fromDto(dto.pickup_details);
  }

  if (dto.dropoff_details) {
    delivery.dropOffLocation = fromDropOffDto(dto.dropoff_details);
  }

  if (dto.driver_details) {
    delivery.driverDetails = dtoToDriverDetails(dto.driver_details);
  }

  if (dto.trip_distance) {
    delivery.distance = dto.trip_distance;
  }

  if (dto.trip_price) {
    delivery.price = dto.trip_price;
  }

  if (dto.currency_model) {
    delivery.priceCurrency = dto.currency_model.display_code;
  }

  if (dto.dropoff_time) {
    delivery.dropOffTime = new Date(dto.dropoff_time);
  }

  if (dto.is_cnf) {
    delivery.isCNF = dto.is_cnf;
    delivery.status = DeliveryStatus.CNF;
  }

  if (dto.driver_notes) {
    delivery.driverNotes = dto.driver_notes?.split('\n')[0];
    delivery.cashOnDelivery = getCODFromDeliveryDetails(dto.driver_notes);
    delivery.orderId = getOrderIdFromDeliveryDetails(dto.driver_notes);
  }

  if (dto.passenger) {
    delivery.passenger = dtoToPassenger(dto.passenger);
  }

  if (dto.vehicle_details) {
    delivery.vehicle = dtoToVehicle(dto.vehicle_details);
  }

  if (dto.delivery_action) {
    delivery.deliveryAction = dto.delivery_action;
  }

  if (dto.booking_type) {
    delivery.bookingType =
      dto.booking_type === DeliveryOptionDto.SCHEDULED ? DeliveryOption.SCHEDULED : DeliveryOption.INSTANT;
  }

  if (dto.expected_pickup_time) {
    delivery.expectedPickupTime = dto.expected_pickup_time;
  }

  if (dto.expected_delivery_window) {
    delivery.expectedDeliveryWindow = dto.expected_delivery_window;
  }

  return delivery;
};

export const activeDeliveryV2DtoToDelivery = (dto: ActiveUserDeliveryV2Dto): Delivery => {
  const delivery: Delivery = {
    id: dto.solutions_reference,
    bookingId: dto.delivery_id,
    status: mapStatus(dto.status),
    pickUpTime: new Date(dto.created_at * 1000)
  };

  if (dto.pickup_details) {
    delivery.pickupLocation = fromDto(dto.pickup_details);
  }

  if (dto.dropoff_details) {
    delivery.dropOffLocation = fromDropOffDto(dto.dropoff_details);
  }

  if (dto.driver_details) {
    delivery.driverDetails = dtoToDriverDetails(dto.driver_details);
  }

  if (dto.driver_notes) {
    delivery.driverNotes = dto.driver_notes.split('\n')[0];
    delivery.cashOnDelivery = getCODFromDeliveryDetails(dto.driver_notes);
    delivery.orderId = getOrderIdFromDeliveryDetails(dto.driver_notes);
  }

  if (dto.passenger) {
    const parts = dto.passenger.name.split(' ');
    delivery.passenger = {
      firstName: parts[0],
      lastName: parts.slice(1).join(' ') || '',
      phoneNumber: dto.passenger.phone_number
    };
  }

  if (dto.vehicle_details) {
    delivery.vehicle = dtoToVehicle(dto.vehicle_details);
  }

  if (dto.delivery_action) {
    delivery.deliveryAction = dto.delivery_action;
  }

  if (dto.delivery_type) {
    delivery.bookingType = dto.delivery_type === 'SCHEDULED' ? DeliveryOption.SCHEDULED : DeliveryOption.INSTANT;
  }

  if (dto.expected_pickup_time) {
    delivery.expectedPickupTime = dto.expected_pickup_time;
  }

  if (dto.expected_delivery_window) {
    delivery.expectedDeliveryWindow = dto.expected_delivery_window;
  }

  delivery.dropOffLocationEdited = Boolean(dto.drop_off_location_edited);

  return delivery;
};

export function activeDeliveriesPageV2DtoToModel(response: ActiveDeliveriesPageResponseDto): {
  deliveries: Delivery[];
  nextStartKeyId: string | null;
  nextStartKeyTime: number | null;
} {
  return {
    deliveries: [...response.deliveries].sort((a, b) => b.created_at - a.created_at).map(activeDeliveryV2DtoToDelivery),
    nextStartKeyId: response.next_start_key_id,
    nextStartKeyTime: response.next_start_key_time
  };
}

export function dtoToDeliveries(deliveries: DeliveryDto[]): Delivery[] {
  if (!deliveries) {
    return [];
  }
  return deliveries.map(singleDtoToDelivery);
}

export function dtoToDeliveriesTransporter(deliveries: DeliveryDto[]): Delivery[] {
  if (!deliveries) {
    return [];
  }
  return deliveries.map(singleDtoToDelivery);
}

export const filterActiveDeliveriesOnly = (deliveries: Delivery[]): DeliveriesWithPageCount => {
  const resultDeliveries = deliveries.filter(
    delivery =>
      delivery.bookingType !== DeliveryOption.SCHEDULED ||
      (delivery.bookingType === DeliveryOption.SCHEDULED && delivery.status !== DeliveryStatus.Pending)
  );
  return {
    deliveries: resultDeliveries,
    count: deliveries.length
  };
};

export const attachDeliveriesCount = (deliveries: Delivery[]): DeliveriesWithPageCount => {
  return {
    deliveries,
    count: deliveries.length
  };
};

export function deliveryDetailsDtoToPartialDelivery({
  id,
  reference,
  created_at,
  pickup,
  dropoff,
  notes,
  cash_in,
  trip_details,
  captain_details,
  order,
  proof_of_delivery_image_url,
  drop_off_location_edited
}: DeliveryDetailsDto): Partial<Delivery> {
  const delivery: Partial<Delivery> = {
    id: reference,
    bookingId: id,
    createdAt: created_at,
    pickUpTime: new Date(created_at * 1000)
  };

  if (pickup) {
    delivery.pickupLocation = fromDtoV2(pickup);
  }
  if (dropoff) {
    delivery.dropOffLocation = fromDropOffDtoV2(dropoff);
  }
  if (notes) {
    delivery.driverNotes = notes;
  }
  if (cash_in) {
    delivery.cashOnDelivery = cash_in.amount;
  }
  if (order) {
    delivery.orderId = order.reference;
  }

  if (dropoff) {
    const fullName = dropoff?.name?.split(',') || [];
    delivery.passenger = {
      firstName: fullName[0] || '',
      lastName: fullName[1] || ''
    };

    if (dropoff.phone_number) {
      delivery.passenger.phoneNumber = dropoff.phone_number.replace(/^\+/, '');
    }
  }

  if (captain_details) {
    delivery.driverDetails = captainDetailsDtoToDriverDetails(captain_details);
    delivery.vehicle = captainDetailsDtoToVehicle(captain_details);
  }

  if (trip_details) {
    const { delivery_distance_in_Km: distanceKm, pricing } = trip_details;
    delivery.distance = distanceKm;
    delivery.price = pricing ? pricing.trip_cost : undefined;
    delivery.priceCurrency = pricing ? pricing.currency : undefined;
  }

  delivery.proofImageUrl = proof_of_delivery_image_url;
  delivery.dropOffLocationEdited = Boolean(drop_off_location_edited);

  return delivery;
}

export function captainDetailsDtoToDriverDetails(captainDetails: CaptainDetailsDto): DriverDetails {
  return {
    driverName: captainDetails.name,
    driverNumber: captainDetails.cellPhone,
    notesToDriver: '',
    driverPicture: captainDetails.profilePicture
  };
}

export function captainDetailsDtoToVehicle(captainDetails: CaptainDetailsDto): Vehicle {
  return {
    vehicleModel: `${captainDetails.color} ${captainDetails.model}`,
    plateNumber: captainDetails.licensePlate
  };
}

export function pastDeliveryDtoToPartialDelivery(dto: PastDeliveryDto): Partial<Delivery> {
  const {
    reference,
    id,
    created_at,
    notes,
    cash_in,
    order,
    customer_name,
    customer_phoneNumber,
    completed_at,
    pickup_name,
    cancellation_reason,
    delivery_type
  } = dto;

  const delivery: Partial<Delivery> = {
    id: reference,
    bookingId: id,
    createdAt: created_at,
    pickUpTime: new Date(created_at * 1000),
    bookingType: delivery_type === DeliveryOptionDto.SCHEDULED ? DeliveryOption.SCHEDULED : DeliveryOption.INSTANT
  };

  if (notes) {
    delivery.driverNotes = notes;
  }
  if (cash_in) {
    delivery.cashOnDelivery = cash_in.amount;
  }
  if (order) {
    delivery.orderId = order.reference;
  }
  if (customer_name || customer_phoneNumber) {
    const [firstName, ...nameParts] = (customer_name || '').split(' ');
    delivery.passenger = {
      firstName,
      lastName: nameParts.join(' ') || '',
      phoneNumber: customer_phoneNumber
    };
  }
  if (completed_at) {
    delivery.completedAt = completed_at * 1000;
  }
  if (pickup_name) {
    delivery.pickupAddressLine = pickup_name;
  }

  delivery.cancellationReason = cancellation_reason;

  return delivery;
}

export function pastDeliveryDtoToDelivery(pastDeliveryDto: PastDeliveryDto): Delivery {
  const delivery = pastDeliveryDtoToPartialDelivery(pastDeliveryDto);

  delivery.status = mapStatus(pastDeliveryDto.status);

  if (delivery.status === DeliveryStatus.CNF) {
    delivery.isCNF = true;
  }

  return delivery as Delivery;
}

export function pastDeliveriesDtoToDeliveries(deliveries: PastDeliveryDto[]): Delivery[] {
  if (!deliveries) {
    return [];
  }

  return deliveries.map(pastDeliveryDtoToDelivery);
}

export function searchLocationToDropOffDtoV2(
  dropOffLocation: SearchLocation,
  { name, phoneNumber, buildingName }: { name: string; phoneNumber?: string; buildingName?: string }
): DropOffLocationDtoV2 {
  const street = [];
  if (dropOffLocation.street) {
    street.push(dropOffLocation.street);
  }
  if (dropOffLocation.building) {
    street.push(dropOffLocation.building);
  }

  const dropoff: DropOffLocationDtoV2 = {
    name,
    address: {
      latitude: dropOffLocation.location.lat,
      longitude: dropOffLocation.location.lng,
      area: dropOffLocation.area,
      street: street.join(', '),
      building_name: buildingName
    }
  };

  if (phoneNumber) {
    dropoff.phone_number = phoneNumber;
  }

  return dropoff;
}

export function requestToDto(deliveryRequest: DeliveryRequest): DeliveryRequestDto {
  const {
    deliveryDetails,
    cashOnDelivery,
    orderId,
    userPhoneNumber,
    orderType,
    useCredit,
    deliveryAction,
    buildingName,
    paymentMethodOnDelivery,
    deliveryWindow
  } = deliveryRequest;

  const pickupName = deliveryRequest.pickupLocation.nickname;

  const result: DeliveryRequestDto = {
    pickup: {
      name: pickupName,
      notes: deliveryRequest.pickupLocation.note,
      address: {
        latitude: deliveryRequest.pickupLocation.location.lat,
        longitude: deliveryRequest.pickupLocation.location.lng,
        city: deliveryRequest.pickupLocation.city,
        area: deliveryRequest.pickupLocation.area,
        street: `${deliveryRequest.pickupLocation.street}, ${deliveryRequest.pickupLocation.building}`
      }
    },
    payment: {
      payment_id: deliveryRequest.paymentMethodId,
      reference_code: 'Express Portal Deliveries',
      use_credit: useCredit || false,
      payment_type: deliveryRequest.paymentType?.toUpperCase()
    }
  };

  if (userPhoneNumber) {
    result.pickup.phone_number = userPhoneNumber;
  }

  if (deliveryRequest.dropOffLocation) {
    result.dropoff = searchLocationToDropOffDtoV2(deliveryRequest.dropOffLocation, {
      name: deliveryRequest.customerName || '',
      buildingName
    });
  }

  if (deliveryRequest.customerPhoneNumber) {
    result.dropoff = result.dropoff || { name: deliveryRequest.customerName || '' };
    result.dropoff.phone_number = deliveryRequest.customerPhoneNumber;
  }

  if (cashOnDelivery) {
    const paymentOnDelivery = { amount: cashOnDelivery, payment_type: paymentMethodOnDelivery };
    result.cash_in = paymentOnDelivery;
    result.cash_out = paymentOnDelivery;
  }

  if (orderId) {
    result.order = {
      reference: orderId
    };
  }

  if (orderType) {
    result.order = result.order || {};
    result.order.type = orderType;
  }

  if (deliveryDetails) {
    result.notes = deliveryDetails;
  }

  if (deliveryAction) {
    result.delivery_action = deliveryAction;
  }

  if (deliveryWindow) {
    result.delivery_window = deliveryWindow;
  }

  return result;
}

export function createResponseDtoToCreateResponse(createResponse?: CreateDeliveryResponseDto): CreateDeliveryResponse {
  if (!createResponse) {
    return {};
  }
  return {
    id: createResponse.reference,
    bookingId: createResponse.id
  };
}

export function dtoToDeliveryReport(dto?: DeliveryReportDto): DeliveryReport {
  if (!dto) {
    return {};
  }

  return {
    id: dto.id,
    status: dto.status,
    url: dto.url
  };
}

export const dtoToDeliveryPriceEstimation = (
  responseDto: DeliveryPriceEstimationResponseDto
): DeliveryPriceEstimation => {
  return {
    tripCost: responseDto.estimated_trip_cost,
    currency: responseDto.currency
  };
};

export const estimationRequestToDto = (
  orderType: OrderType,
  pickupLatLng: LatLng,
  dropoffLatLng: LatLng
): DeliveryPriceEstimationRequestDto => {
  return {
    type: orderType,
    pickup: {
      coordinate: pickupLatLng
    },
    dropoff: {
      coordinate: dropoffLatLng
    }
  };
};
